import React from "react";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { withStyles } from "@mui/styles";
import RevaText from "components/common/RevaText";

const styles = (theme) => ({
  container: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
  },
  headCell: {
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  emptyCell: {
    padding: theme.spacing(4),
    textAlign: "center",
  },
  clickable: {
    cursor: "pointer",
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: -2,
    },
  },
});

// Clicks that start on a real control inside a clickable row (links, buttons, inputs) belong to that control.
const INTERACTIVE_SELECTOR = "a, button, input, select, textarea, label, [role='button'], [data-row-click-ignore]";

function GestionTable({ headers = [], rows = [], emptyMessage, getRowSx, onRowClick, getRowLabel, classes }) {
  if (!Array.isArray(headers) || headers.length === 0) return null;

  const handleRowClick = (event, row) => {
    if (event.defaultPrevented) return;
    const interactive = event.target.closest?.(INTERACTIVE_SELECTOR);
    if (interactive && event.currentTarget.contains(interactive)) return;
    onRowClick(row);
  };

  // Enter / Space on the focused row itself (not on a control inside it) opens it.
  const handleRowKeyDown = (event, row) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onRowClick(row);
    }
  };

  const renderCell = (value, row, header) => {
    if (header?.renderCell) {
      return header.renderCell(value, row);
    }
    if (React.isValidElement(value)) {
      return value;
    }
    if (value == null || value === "") {
      return "—";
    }
    return <RevaText>{value}</RevaText>;
  };

  return (
    <TableContainer component={Paper} className={classes.container}>
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell
                key={header.key || header.title || index}
                align={header.align || "left"}
                className={classes.headCell}
                style={{ width: header.width, minWidth: header.minWidth }}
              >
                {header.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={headers.length} className={classes.emptyCell}>
                {emptyMessage || "—"}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row, rowIndex) => (
              <TableRow
                key={row.id ?? row.key ?? rowIndex}
                hover
                className={onRowClick ? classes.clickable : undefined}
                sx={getRowSx ? getRowSx(row) : undefined}
                onClick={onRowClick ? (event) => handleRowClick(event, row) : undefined}
                onKeyDown={onRowClick ? (event) => handleRowKeyDown(event, row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                // Keeps the row semantics (so table navigation still works) while naming the action.
                aria-label={onRowClick && getRowLabel ? getRowLabel(row) : undefined}
              >
                {headers.map((header, colIndex) => (
                  <TableCell
                    key={`${rowIndex}-${header.key || colIndex}`}
                    align={header.align || "left"}
                    padding={header.padding}
                    // Cells such as "Acciones" never trigger the row click, whatever they contain.
                    onClick={header.stopRowClick ? (event) => event.stopPropagation() : undefined}
                    onKeyDown={header.stopRowClick ? (event) => event.stopPropagation() : undefined}
                  >
                    {renderCell(row[header.key], row, header)}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default withStyles(styles)(GestionTable);
